import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

/**
 * ProtectedRoute component to handle route protection based on retailer's plan status.
 *
 * @param {Object} props - The component props.
 * @param {React.ElementType} props.element - The component to render if the route is accessible.
 * @param {boolean} props.superAdminOnly - If true, only super_admin users can access this route.
 * @param {...Object} rest - The rest of the props to pass to the component.
 * @returns {React.Element} - The rendered component or a redirect to the plans page.
 */
const ProtectedRoute = ({
  element: Component,
  superAdminOnly = false,
  requiredPermission = null,
  requiredFeatureFlag = null,
  ...rest
}) => {
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const { currentRetailerUser } = useSelector((reduxState) => reduxState.mainReducer);
  const { 
    slug, 
    plan_status, 
    chargebee_plan_id, 
    chargebee_subscription_id,
    free_trial_days_remaining,
    current_plan,
    plan_charge_attempt
  } = retailer_info || {};

  // Verificar acceso solo para super_admin
  if (superAdminOnly && !currentRetailerUser?.super_admin) {
    return <Navigate to={`/retailers/${slug}/dashboard`} replace />;
  }

  const requiredPermissionKey = requiredPermission ? `can_${requiredPermission}` : null;
  if (requiredPermissionKey && currentRetailerUser && currentRetailerUser[requiredPermissionKey] !== true) {
    return <Navigate to={`/retailers/${slug}/dashboard`} replace />;
  }

  if (requiredFeatureFlag && retailer_info && retailer_info[requiredFeatureFlag] !== true) {
    return <Navigate to={`/retailers/${slug}/dashboard`} replace />;
  }

  const isAcademyRoute = window.location.pathname.includes('/academy');

  // Academy siempre es accesible, incluso con plan expirado
  if (isAcademyRoute) {
    return <Component {...rest} />;
  }

  if (retailer_info && plan_status === 'inactive') {
    if (window.location.pathname.includes('/pricing')) {
      return <Component {...rest} />;
    }
    return <Navigate to={`/retailers/${slug}/pricing`} replace />;
  }

  const freeTrialEnded = free_trial_days_remaining === 0 && current_plan === 'free';
  const isDashboardRoute = window.location.pathname.includes('/dashboard');

  if (retailer_info && freeTrialEnded && !isDashboardRoute && !window.location.pathname.includes('/plans')) {
    return <Navigate to={`/retailers/${slug}/dashboard`} replace />;
  }

  return <Component {...rest} />;
};

export default ProtectedRoute;
